import { AppFeatureParams } from 'src/types/AppFeatureParams';
import { NoonReportStatusEnum, NoonReportTypeEnum } from '../../types/ViewModel/AolEnums';
import { AolDataItem, AolGroup, AolItem, AolValidationItem } from '../../types/ViewModel/AolItem';
import { ValidatedNoonReport } from '../../types/ViewModel/ValidatedNoonReport';
import { Vessel } from '../../types/ViewModel/Vessel/Vessel';
import { formatAolGroupDate, getAolDateLocalTime, getAolGroupDate } from '../../utils/aol/aolDateUtils';
import { getAolDateFromTime, getAolGroupStatus } from '../../utils/aolFieldsHelper';
import { convertToDate } from 'odfjell-date-time-utils/dateTimeConverter';
import { DateTimeFormat, getDateFromFormat } from 'odfjell-date-time-utils/dateTimeFormatter';
import {
    addDateHours,
    dateToUtc,
    getDateAsUtc,
    getDateNow,
    getDiffBetweenDates,
    isDateAfter,
    isDateBefore,
    isDateSame,
} from 'odfjell-date-time-utils/dateTimeUtils';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { mapOfflineAolItemsForNewReports, mapOfflineNoonReports } from './OfflineNoonReportFactory';
import { isAolApproved, isAolCompleted } from 'src/components/aol/AolUtils';
import { getAllAolItems } from 'src/api/aol/offlineAolApi';
import { getCargoOnBoard } from 'src/utils/aol/aolCargoUtils';
import { getEffectiveAolStartDate } from 'src/utils/aol/aolStartDateUtils';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';

export const makeAolDataItemList = async (
    aolItems: AolItem[],
    noonReports: ValidatedNoonReport[],
    isFeatureEnabled: (appFeatureParams: AppFeatureParams) => boolean,
    vessel: Vessel,
    stowagePlan: StowagePlan | null
) => {
    let aolDataItems: AolDataItem[] = [];
    let fullyOfflineNoonReports: ValidatedNoonReport[] = [];

    const hasAolItems = aolItems && aolItems.length > 0;

    if (hasAolItems) {
        aolItems.sort((a, b) => a.portLogAolItem.startTimeLocalTime.getTime() - b.portLogAolItem.startTimeLocalTime.getTime());
        setCargoOnBoard(aolItems, stowagePlan, isFeatureEnabled, vessel);
        const fromDate = getAolDateFromTime(aolItems[0].portLogAolItem.startTimeLocalTime);
        const toDate = getAolDateFromTime(aolItems[aolItems.length - 1].portLogAolItem.startTimeLocalTime);

        const offlineAolItems = await getAllAolItems();
        fullyOfflineNoonReports = await mapOfflineAolItemsForNewReports(offlineAolItems, fromDate, toDate);
        noonReports = mapOfflineNoonReports(offlineAolItems, fullyOfflineNoonReports, noonReports, fromDate, toDate);
    }

    noonReports.map((noonReport, i) => {
        if (noonReport) {
            const reportDate = noonReport.generalSection.noonReportDateTime.aolDateLocalTime;
            let aolItem: AolItem | undefined;
            if (noonReport.metadata.aolVersion > 2) {
                const fromTime = noonReport.generalSection.noonReportDateTime.reportFromTimeLocalTime;
                const toTime = noonReport.generalSection.noonReportDateTime.reportToTimeLocalTime;
                aolItem = aolItems.find(
                    (a) =>
                        isDateSame(a.portLogAolItem.startTimeLocalTime, fromTime, 'minute') &&
                        isDateSame(a.portLogAolItem.stopTimeLocalTime, toTime, 'minute')
                );
            } else {
                if (!aolItem) {
                    aolItem = aolItems.find((a) =>
                        isDateSame(getAolDateLocalTime(reportDate), getAolDateFromTime(a.portLogAolItem.startTimeLocalTime), 'day')
                    );
                }
            }
            const dstHours = aolItem?.dstOffsetHours && aolItem.dstOffsetHours !== 0 ? aolItem.dstOffsetHours : 0;
            const portName = getPortName(aolItem, noonReport);
            const aolValidationItems = isReportValid(noonReport, aolItem);
            const reportHours = noonReport.metadata.aolVersion > 2 ? noonReport.generalSection.hours : noonReport.generalSection.hoursWithOffset;
            const aolDataItem: AolDataItem = {
                aolItem,
                noonReport,
                reportDate: convertToDate(reportDate, 'yyyy-MM-dd'),
                portName: portName?.toUpperCase(),
                reportType: noonReport.metadata.noonReportType,
                status: reportStatus(noonReport),
                id: i + 1,
                aolValidationItems,
                reportHours,
                reportHoursWithoutDst: reportHours - dstHours,
                isValid: true,
                aolVersion: noonReport.metadata.aolVersion,
                reportFromTimeLocalTime: noonReport.generalSection.noonReportDateTime.reportFromTimeLocalTime,
                reportToTimeLocalTime: noonReport.generalSection.noonReportDateTime.reportToTimeLocalTime,
                reportFromTimeUtc: noonReport.generalSection.noonReportDateTime.reportFromTimeUTC,
                reportToTimeUtc: noonReport.generalSection.noonReportDateTime.reportToTimeUTC,
                isReportAtNoon: false,
                indexInDay: 0,
                numberOfReportsInDay: 0,
                aolDataItemProperties: {},
                simpleModeAol: noonReport.metadata.simpleModeAol,
                useNewConsumptionModel: isFeatureEnabled({
                    featureEnum: AppFeatureEnum.Bunkering,
                    date: convertToDate(reportDate, 'yyyy-MM-dd'),
                    imoNumber: vessel.imoNumber,
                }),
            };

            if (noonReport.hasOfflineData) {
                noonReport.metadata.isAolComplete = isAolCompleted(aolDataItem, vessel);
                noonReport.metadata.isAolApproved = isAolApproved(aolDataItem, vessel);
                aolDataItem.status = reportStatus(noonReport);
            }
            aolDataItems.push(aolDataItem);
        }
    });

    let index = aolDataItems.length + 1;
    let cumulativeTimezoneAdjustmentHours = 0;
    const missingAolDataItems = (aolItems || []).reduce((missingList: AolDataItem[], aolItem) => {
        const reportDate = getAolDateFromTime(aolItem.portLogAolItem.startTimeLocalTime);

        let noonReport = findNoonReportByTime(
            fullyOfflineNoonReports,
            aolItem.portLogAolItem.startTimeLocalTime,
            aolItem.portLogAolItem.stopTimeLocalTime
        );

        if (!noonReport) {
            noonReport = findNoonReportByTime(noonReports, aolItem.portLogAolItem.startTimeLocalTime, aolItem.portLogAolItem.stopTimeLocalTime);
        }

        const portName = getPortName(aolItem, noonReport);
        const timeZoneChangeHours = aolItem.timeZoneChangeHours;
        const dstOffsetHours = aolItem.dstOffsetHours;

        // Compute hours between local start/stop (works for normal single-timezone items)
        const localTimeDiffHours = getDiffBetweenDates(
            dateToUtc(aolItem.portLogAolItem.stopTimeLocalTime),
            dateToUtc(aolItem.portLogAolItem.startTimeLocalTime),
            'hour'
        ).hours;

        let reportHoursWithoutTimeChanges: number;
        if (localTimeDiffHours <= 0 && aolItem.portLogAolItem.startTimeUtc && aolItem.portLogAolItem.stopTimeUtc) {
            // IDL crossing: local times are equal or inverted (different timezones).
            // Use actual UTC times for the correct physical time elapsed.
            reportHoursWithoutTimeChanges = getDiffBetweenDates(
                new Date(aolItem.portLogAolItem.stopTimeUtc),
                new Date(aolItem.portLogAolItem.startTimeUtc),
                'hour'
            ).hours;
        } else {
            // Normal case: local diff + timezone change gives correct physical time
            reportHoursWithoutTimeChanges = localTimeDiffHours + timeZoneChangeHours;
        }
        const reportHours = reportHoursWithoutTimeChanges + dstOffsetHours;
        const reportHoursWithoutDst = reportHoursWithoutTimeChanges;

        const _useNewConsumptionModel = isFeatureEnabled({
            featureEnum: AppFeatureEnum.Bunkering,
            date: convertToDate(reportDate, 'yyyy-MM-dd'),
            imoNumber: vessel.imoNumber,
        });

        if (!noonReport) {
            const aolDataItem: AolDataItem = {
                aolItem,
                noonReport,
                portName,
                reportType: aolItem.portLogAolItem.noonReportType,
                status: NoonReportStatusEnum.Missing,
                id: index,
                isValid: aolItem.portLogAolItem.isValid,
                reportHours,
                reportHoursWithoutDst,
                aolVersion: 5,
                reportDate,
                reportFromTimeLocalTime: aolItem.portLogAolItem.startTimeLocalTime,
                reportToTimeLocalTime: aolItem.portLogAolItem.stopTimeLocalTime,
                reportFromTimeUtc: (localTimeDiffHours <= 0 && aolItem.portLogAolItem.startTimeUtc)
                    ? new Date(aolItem.portLogAolItem.startTimeUtc)
                    : addDateHours(dateToUtc(aolItem.portLogAolItem.startTimeLocalTime), cumulativeTimezoneAdjustmentHours),
                reportToTimeUtc: (localTimeDiffHours <= 0 && aolItem.portLogAolItem.stopTimeUtc)
                    ? new Date(aolItem.portLogAolItem.stopTimeUtc)
                    : addDateHours(dateToUtc(aolItem.portLogAolItem.stopTimeLocalTime), cumulativeTimezoneAdjustmentHours + timeZoneChangeHours + dstOffsetHours),
                isReportAtNoon: false,
                indexInDay: 0,
                numberOfReportsInDay: 0,
                aolDataItemProperties: {},
                simpleModeAol: vessel.hasOrcaWebSimpleMode,
                useNewConsumptionModel: _useNewConsumptionModel,
            };

            missingList.push(aolDataItem);
            index++;
        }
        cumulativeTimezoneAdjustmentHours += timeZoneChangeHours + dstOffsetHours;
        return missingList;
    }, []);

    aolDataItems.forEach((a) => {
        if (a.isValid && a.aolItem) {
            a.isValid = a.aolItem.portLogAolItem.isValid;
        }
    });
    aolDataItems = aolDataItems.concat(missingAolDataItems);

    return aolDataItems;
};

export const makeAolGroupList = (aolDataItems: AolDataItem[], vessel: Vessel) => {
    const aolGroupsArray: AolGroup[] = [];
    const aolStartDate = getEffectiveAolStartDate(vessel);
    const visibleAolDataItems = aolStartDate
        ? aolDataItems.filter((aolDataItem) => !isDateBefore(aolDataItem.reportDate, aolStartDate, 'day'))
        : aolDataItems;

    const aolGroups = visibleAolDataItems.reduce((aolGroups, aolDataItem) => {
        const aolDate = formatAolGroupDate(aolDataItem.reportDate);
        (aolGroups[aolDate] = aolGroups[aolDate] || []).push(aolDataItem);

        return aolGroups;
    }, {});

    Object.entries(aolGroups).forEach(([aolDateGroup]) => {
        let aolDataItems = aolGroups[aolDateGroup];
        let aolHours = 0;
        if (aolDataItems && aolDataItems.length > 0) {
            aolDataItems = aolDataItems.sort((a, b) => {
                const aDate = a.reportFromTimeLocalTime ?? a?.reportDate;
                const bDate = b.reportFromTimeLocalTime ?? b?.reportDate;
                const unitOfTime = a.reportFromTimeLocalTime && b.reportFromTimeLocalTime ? 'minute' : 'day';
                if (isDateAfter(aDate, bDate, unitOfTime)) return 1;
                else return -1;
            });
            aolHours = aolDataItems.reduce((hours, aolDataItem) => (hours += aolDataItem.reportHours), 0);
            const maxAolVersion = aolDataItems.reduce(
                (version, aolDataItem) => (version = aolDataItem.aolVersion > version ? aolDataItem.aolVersion : version),
                0
            );

            if (aolDataItems.length > 1) {
                const dataItemWithDst = aolDataItems.find(
                    (a) => a.aolItem?.dstOffsetHours && a.aolItem.dstOffsetHours !== 0 && !Object.is(a.aolItem.dstOffsetHours, -0)
                );
                if (dataItemWithDst) {
                    aolHours = aolDataItems.reduce(
                        (hours, aolDataItem) => (hours += aolDataItem.reportHoursWithoutDst + (aolDataItem.aolItem?.dstOffsetHours ?? 0)),
                        0
                    );
                }
            }

            aolDataItems.forEach((a, i) => {
                a.indexInDay = i;
                a.numberOfReportsInDay = aolDataItems.length;
                if (a.aolVersion !== maxAolVersion) {
                    a.aolVersion = maxAolVersion;
                }
            });

            if (aolDataItems.length > 0) {
                aolDataItems[aolDataItems.length - 1].isReportAtNoon = true;
            }
        }

        const status = getAolGroupStatus(aolDataItems);
        const isValid = getGroupDateIsValid(aolDataItems);
        const aolVersion = getAolGroupVersion(aolDataItems);
        const hasTankersEventMismatch = aolDataItems.some((a) => a.aolValidationItems);
        const aolDate = formatAolGroupDate(aolGroups[aolDateGroup][0].reportDate);
        const isBeforeOrcaWebStartDate = vessel?.orcaWebStartDate
            ? isDateBefore(getDateFromFormat(aolDate, DateTimeFormat.queryFormats.dateOnly), vessel.orcaWebStartDate, 'day')
            : false;
        const aolGroup: AolGroup = {
            aolDate,
            aolDataItems: aolDataItems,
            status: status,
            aolHours,
            isValid: isValid,
            aolVersion: aolVersion,
            hasTankersEventMismatch,
            isBeforeOrcaWebStartDate,
        };

        aolGroupsArray.push(aolGroup);
        return aolGroupsArray;
    });

    return aolGroupsArray.sort((a, b) => (isDateAfter(getAolGroupDate(a), getAolGroupDate(b), 'day') ? 1 : -1));
};

const findNoonReportByTime = (noonReports: ValidatedNoonReport[], startTime: Date, stopTime: Date) =>
    noonReports.find(
        (n) =>
            isDateSame(n.generalSection.noonReportDateTime.reportFromTimeLocalTime, startTime, 'minute') &&
            isDateSame(n.generalSection.noonReportDateTime.reportToTimeLocalTime, stopTime, 'minute')
    );

const getGroupDateIsValid = (aolDataItems: AolDataItem[]): boolean => {
    return aolDataItems.filter((a) => !(a.aolItem?.portLogAolItem?.isValid ?? a.isValid)).length === 0;
};
const getAolGroupVersion = (aolDataItems: AolDataItem[]): number => {
    return aolDataItems.length > 0 ? aolDataItems[0].aolVersion : 3;
};

const getPortName = (aolItem: AolItem | undefined, noonReport: ValidatedNoonReport | undefined) => {
    if (noonReport && noonReport.metadata.noonReportType === NoonReportTypeEnum.Sea) return null;

    if (aolItem?.portDescription) {
        return aolItem.portDescription?.toUpperCase();
    }

    if (noonReport && aolItem?.lastPortVisits && aolItem?.lastPortVisits.length > 0) {
        const lastPortVisits = aolItem?.lastPortVisits.find((l) => l.noonReportId === noonReport?.metadata.noonReportId && l.portName);
        if (lastPortVisits) return lastPortVisits.portName?.toUpperCase();
    }

    return null;
};

const reportStatus = (noonReport: ValidatedNoonReport) => {
    return noonReport.metadata.isAolApproved
        ? NoonReportStatusEnum.Approved
        : noonReport.metadata.isAolComplete
        ? NoonReportStatusEnum.Completed
        : NoonReportStatusEnum.Edited;
};

const isReportValid = (noonReport: ValidatedNoonReport, aolItem?: AolItem) => {
    const portLogAolItem = aolItem?.portLogAolItem;
    if (noonReport.metadata.aolVersion < 3) return;
    const validationItems: AolValidationItem[] = [];

    if (portLogAolItem) {
        const minorMismatchHourMargin = 0.1;
        const timeZoneChangeHours = aolItem ? aolItem.timeZoneChangeHours : 0;
        const dstOffsetHours = aolItem ? aolItem.dstOffsetHours : 0;
        const timeDiff = getDiffBetweenDates(
            getDateAsUtc(portLogAolItem.stopTimeLocalTime),
            getDateAsUtc(portLogAolItem.startTimeLocalTime),
            'hour'
        ).hours;
        const aolItemHours = timeDiff + timeZoneChangeHours + dstOffsetHours;
        const isNoonReportTypeValid = noonReport.metadata.noonReportType === portLogAolItem.noonReportType;
        const isNumberOfHoursValid = Math.abs(noonReport.generalSection.hours - aolItemHours) < minorMismatchHourMargin;
        const isImosPortCallIdValid = noonReport.metadata.imosPortCallId === portLogAolItem.imosPortCallId;
        if (!isImosPortCallIdValid) {
            validationItems.push({
                type: 'imosPortCallId',
                data: {
                    noonReportPortCallId: noonReport.metadata.imosPortCallId,
                    portLogPortCallId: portLogAolItem.imosPortCallId,
                },
                noonReport,
            });
        }
        if (!isNumberOfHoursValid) {
            validationItems.push({
                type: 'numberOfHours',
                data: { noonReportHours: noonReport.generalSection.hours, portLogHours: aolItemHours },
                noonReport,
            });
        }
        if (!isNoonReportTypeValid) {
            validationItems.push({
                type: 'noonReportType',
                data: {
                    noonReportType: noonReport.metadata.noonReportType,
                    portLogType: portLogAolItem.noonReportType,
                },
                noonReport,
            });
        }
    } else {
        validationItems.push({
            type: 'missingPortLogAolItem',
            data: {
                noonReportId: noonReport.metadata.noonReportId,
            },
            noonReport,
        });
    }
    return validationItems.length > 0 ? validationItems : null;
};

const setCargoOnBoard = (
    aolItems: AolItem[],
    stowagePlan: StowagePlan | null,
    isFeatureEnabled: (appFeatureParams: AppFeatureParams) => boolean,
    vessel: Vessel
) => {
    if (isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: vessel.imoNumber, date: getDateNow() }) && stowagePlan) {
        aolItems.forEach((aolItem) => {
            if (+aolItem.voyageNumber >= vessel.firstVoyageOrcaWebStowageImosSync) {
                aolItem.cargoOnBoard = getCargoOnBoard(aolItem.portLogAolItem.noonReportType, aolItem.portLogAolItem.imosPortCallId, stowagePlan);
            }
        });
    }
};
